import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchProfile, updateProfile, fetchAliasHistory, type Profile, type AliasHistoryEntry } from '../../features/profile/api';
import { fetchUserScore } from '../../features/ranking/api';
import { Button } from '../../components/ui/Button';
import { AliasManager } from '../../components/alias/AliasManager';
import { useAuth } from '../../contexts/AuthContext';

export function ProfilePage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [aliasHistory, setAliasHistory] = useState<AliasHistoryEntry[]>([]);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [showAliasHistory, setShowAliasHistory] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }

    if (user?.username) {
      loadProfile();
    }
  }, [isAuthenticated, user, navigate]);

  async function loadProfile() {
    if (!user?.username) return;

    try {
      setLoading(true);
      setError(null);
      const [profileData, aliasData, statsData] = await Promise.all([
        fetchProfile(user.username).catch(async (err) => {
          // If profile not found, create a default one
          if (err.message.includes('profile not found') || err.message.includes('404')) {
            console.log('Profile not found, creating default profile...');
            const defaultProfile: Profile = {
              username: user.username,
              displayName: user.username,
              bio: '',
              activeAlias: '',
              marketingOptIn: false,
            };
            // Try to create the profile
            try {
              return await updateProfile(user.username, defaultProfile);
            } catch {
              // If creation fails, just return the default
              return defaultProfile;
            }
          }
          throw err;
        }),
        fetchAliasHistory(user.username).catch(() => []),
        fetchUserScore(user.username).catch(() => ({ values: {} })),
      ]);

      // Get the most recent alias from alias history (auth service has the real data)
      let currentAlias = profileData.activeAlias;
      if (aliasData && aliasData.length > 0) {
        // Sort by timestamp descending and get the most recent
        const sortedAliases = [...aliasData].sort((a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
        currentAlias = sortedAliases[0].alias;
        // Update profile with the current alias from auth service
        profileData.activeAlias = currentAlias;
      }

      setProfile(profileData);
      setAliasHistory(aliasData);
      setStats(statsData.values || {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }

  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Header */}
      <div className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">My Profile</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">{user?.username}</p>
            </div>
            <Button
              onClick={loadProfile}
              variant="secondary"
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center text-slate-600 dark:text-slate-400">Loading profile...</div>
        ) : error ? (
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400">
            Error: {error}
            <Button onClick={loadProfile} className="mt-4">
              Retry
            </Button>
          </div>
        ) : profile ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Profile Section */}
            <div className="lg:col-span-2">
              <ProfileCard
                profile={profile}
                editMode={editMode}
                onToggleEdit={() => setEditMode(!editMode)}
                onSave={async (updated) => {
                  if (!user?.username) return;
                  try {
                    setLoading(true);
                    const saved = await updateProfile(user.username, updated);
                    setProfile(saved);
                    setEditMode(false);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to update profile');
                  } finally {
                    setLoading(false);
                  }
                }}
              />

              {/* Alias History */}
              <div className="mt-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Alias History</h2>
                  <button
                    onClick={() => setShowAliasHistory(!showAliasHistory)}
                    className="text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-500"
                  >
                    {showAliasHistory ? 'Hide' : 'Show'}
                  </button>
                </div>
                {showAliasHistory && (
                  <>
                    {aliasHistory.length === 0 ? (
                      <p className="text-slate-500 dark:text-slate-500 text-sm">No alias history yet</p>
                    ) : (
                      <div className="space-y-2">
                        {aliasHistory.map((entry, idx) => (
                          <div key={idx} className="bg-slate-50 dark:bg-slate-900 rounded px-3 py-2 flex items-center justify-between border border-slate-200 dark:border-slate-800">
                            <div>
                              <div className="font-medium text-slate-900 dark:text-slate-200">{entry.alias}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-500">Topic: {entry.topicId}</div>
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-500">
                              {new Date(entry.timestamp).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Sidebar - Stats & Badges */}
            <div className="space-y-6">
              <QuickLinksCard />
              <StatsCard profile={profile} stats={stats} />
              <BadgesCard />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ProfileCard({
  profile,
  editMode,
  onToggleEdit,
  onSave,
}: {
  profile: Profile;
  editMode: boolean;
  onToggleEdit: () => void;
  onSave: (updated: Partial<Profile>) => void;
}) {
  const [displayName, setDisplayName] = useState(profile.displayName);
  const [bio, setBio] = useState(profile.bio);
  const [activeAlias, setActiveAlias] = useState(profile.activeAlias);

  useEffect(() => {
    setDisplayName(profile.displayName);
    setBio(profile.bio);
    setActiveAlias(profile.activeAlias);
  }, [profile]);

  function handleSave() {
    onSave({
      displayName,
      bio,
      activeAlias,
    });
  }

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Profile Information</h2>
        <Button onClick={onToggleEdit} variant="secondary">
          {editMode ? 'Cancel' : 'Edit'}
        </Button>
      </div>

      {editMode ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={4}
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>

          <div>
            <AliasManager
              currentAlias={activeAlias}
              onAliasChanged={(newAlias) => setActiveAlias(newAlias)}
              showInline={true}
            />
          </div>

          <Button onClick={handleSave} className="w-full">
            Save Changes
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-slate-600 dark:text-slate-400">Display Name</div>
            <div className="text-slate-900 dark:text-slate-100">{profile.displayName || 'Not set'}</div>
          </div>

          <div>
            <div className="text-sm font-medium text-slate-600 dark:text-slate-400">Bio</div>
            <div className="text-slate-900 dark:text-slate-100 whitespace-pre-wrap">{profile.bio || 'No bio yet'}</div>
          </div>

          <div>
            <div className="text-sm font-medium text-slate-600 dark:text-slate-400">Active Alias</div>
            <div className="text-slate-900 dark:text-slate-100">{profile.activeAlias || 'Not set'}</div>
          </div>

          {profile.createdAt && (
            <div>
              <div className="text-sm font-medium text-slate-600 dark:text-slate-400">Member Since</div>
              <div className="text-slate-900 dark:text-slate-100">{new Date(profile.createdAt).toLocaleDateString()}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function QuickLinksCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [moderationStatus, setModerationStatus] = useState<{ isBanned: boolean; isMuted: boolean; banReason?: string; muteReason?: string } | null>(null);

  useEffect(() => {
    if (user?.username) {
      import('../../services/moderation').then(({ getUserStatus }) => {
        getUserStatus(user.username).then(setModerationStatus).catch(() => {});
      });
    }
  }, [user]);

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Quick Links</h2>
      <div className="space-y-2">
        <button
          onClick={() => navigate('/appeals')}
          className="w-full text-left px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-100 transition-colors border border-slate-200 dark:border-slate-700"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">⚖️</span>
              <div>
                <div className="font-medium text-sm">My Appeals</div>
                <div className="text-xs text-slate-600 dark:text-slate-400">View and manage appeals</div>
              </div>
            </div>
            {moderationStatus?.isBanned && (
              <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
                Banned
              </span>
            )}
            {moderationStatus?.isMuted && !moderationStatus?.isBanned && (
              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded">
                Muted
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => navigate('/messages')}
          className="w-full text-left px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-100 transition-colors border border-slate-200 dark:border-slate-700"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">💬</span>
            <div>
              <div className="font-medium text-sm">Messages</div>
              <div className="text-xs text-slate-600 dark:text-slate-400">Private conversations</div>
            </div>
          </div>
        </button>
        <button
          onClick={() => navigate('/marketplace/seller')}
          className="w-full text-left px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-100 transition-colors border border-slate-200 dark:border-slate-700"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">🏪</span>
            <div>
              <div className="font-medium text-sm">My Listings</div>
              <div className="text-xs text-slate-600 dark:text-slate-400">Manage marketplace listings</div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

function StatsCard({ profile, stats }: { profile: Profile; stats: Record<string, number> }) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Activity Stats</h2>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-slate-600 dark:text-slate-400">Forum Posts</span>
          <span className="text-slate-900 dark:text-slate-100 font-semibold">{stats.posts || 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-600 dark:text-slate-400">Forum Replies</span>
          <span className="text-slate-900 dark:text-slate-100 font-semibold">{stats.replies || 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-600 dark:text-slate-400">Marketplace Listings</span>
          <span className="text-slate-900 dark:text-slate-100 font-semibold">{stats.listings || 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-600 dark:text-slate-400">Completed Bookings</span>
          <span className="text-slate-900 dark:text-slate-100 font-semibold">{stats.bookings || 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-600 dark:text-slate-400">Reputation Score</span>
          <span className="text-brand-600 dark:text-brand-400 font-semibold">{stats.reputation || 0}</span>
        </div>
      </div>
    </div>
  );
}

function BadgesCard() {
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Badges & Achievements</h2>
      <div className="text-center py-8 text-slate-500 dark:text-slate-500">
        <div className="text-4xl mb-2">🏆</div>
        <p className="text-sm">No badges earned yet</p>
        <p className="text-xs mt-1">Participate in the community to earn badges!</p>
      </div>
    </div>
  );
}
